/**
 * Crest-line extraction dialog: pick a guide, tune the corridor and advanced
 * parameters, optionally clip the analysis to a boundary layer, watch the live
 * preview stats update, then save the extracted runs as real features
 * (Task 10). A fully successful save closes the dialog; the success toast
 * carries an *Export as DXF* action that hands the saved feature ids off via
 * `crestExportRequestAtom`, which `Layers.tsx` reads to mount a standalone
 * `VectorExportDialog` scoped to just the features this save created (Task
 * 12 — the customer's second deliverable, a downloadable 3D polyline). That
 * export flow has to live outside this component because closing on success
 * unmounts it, so it can no longer host its own export dialog the way an
 * earlier revision did. A mid-batch failure does NOT close the dialog — see
 * `savedRunsRef` below for why staying open is what makes a retry resumable
 * instead of duplicating the runs that already succeeded.
 * That button only exists because spec §14.2 was verified first: `geometryZ`
 * columns are 3D end to end, `ST_AsGeoJSON` never drops the Z ordinate, and
 * `ogr2ogrArgs('DXF', …)` (processDWGGDAL/exportUtils.ts) passes no
 * `-dim`/flattening flag, so a crest line's varying elevation reaches GDAL's
 * DXF writer intact.
 *
 * Built on the house form layer — `Form`/`FormField`/`FormItem`/`FormLabel`/
 * `FormControl`/`FormDescription`/`FormMessage` over react-hook-form + zod,
 * with `DialogHeader`+`DialogDescription` and the actions in a `DialogFooter`
 * — exactly like `RunCompareSurfacesDialog`.
 * An earlier revision used bare `<Label>`s and plain divs on the theory that
 * `form.watch()` goes stale; that trap is real but is specific to a `form`
 * passed as a PROP into a shared field component (`StationVolumesFields`,
 * `ExpectationFields`), which does not apply to a self-contained dialog. Live
 * values for the preview come from `useWatch`, which is the supported way to
 * subscribe a render to form state.
 *
 * Numeric inputs use NumericField, never `<Input type="number">`: plain number
 * inputs combined with `parseFloat` in `onChange` make "-", "-0" and "-0."
 * impossible to type.
 *
 * Save writes ONE `LineString` feature per run, never a `MultiLineString`
 * across runs: `featureToAlignment` (`@/core/featureAlignment.ts`) calls
 * `concatMultiLine` on a MultiLineString's parts, which would weld separate
 * runs across the real surface gaps between them into one bogus alignment.
 * `properties.alignment` is what makes the saved line a first-class
 * alignment — its presence is what `GeoJson.tsx` looks for to register a
 * feature into `alignmentsAtom`. `properties.crest` records provenance
 * (source layer, the surfaces the sampler spanned, the parameter set used,
 * the boundary layer whose clip was ACTUALLY APPLIED — never merely the one
 * the picker was showing, see `appliedBoundary` — and a timestamp) so a saved
 * line can be traced back to how it was made.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { alignmentsAtom } from '@/atoms/alignment.atom';
import {
  CREST_GUIDE_MIN_VERTICES,
  crestExportRequestAtom,
  crestGuideCancelAtom,
  crestGuideDraftAtom,
  crestGuideModeAtom,
  crestPreviewAtom,
  crestSourcesAtom,
} from '@/atoms/crestLine.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { Button } from '@/components/ui/button';
import { AdvancedSection, AnalysisSection, INPUT_ROLE_LABEL_KEYS, OutputSummary } from '@/components/analyses/sections';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { NumericField } from '@/components/ui/numeric-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { execute } from '@/graphql/execute';
import { LayerType } from '@/graphql/graphql';
import { useFeature, useFeatureCheck } from '@/hooks/useFeature';
import { boundaryPolygonsFromFeatures, makeInsideBoundary } from '@/lib/crestLine/boundary';
import { extractCrestLine } from '@/lib/crestLine/extract';
import { guideFromAlignment, guideFromXY } from '@/lib/crestLine/guide';
import {
  DEFAULT_CREST_PARAMS,
  type CrestParams,
  type CrestResult,
  type CrestWarning,
  type Guide,
} from '@/lib/crestLine/types';
import { buildLineGeometry, projectPartsToWGS84, type Coord3 } from '@/lib/featureGeometry';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { useDialog } from '@/providers/DialogProvider';
import { createFeatureMutation } from '@/queries/feature.query';
import { createLayerMutation } from '@/queries/layer.query';

export interface CrestLineDialogProps {
  /** The layer registered under `crestSourcesAtom` to extract the crest from. */
  sourceLayerId: string;
  projectId: string;
  /**
   * The project's SRID, needed to tag the saved geometry's CRS and to
   * reproject it to WGS84 for `geometryWGS84`. Threaded down as a prop —
   * same pattern as `AddLineToolbar`/`AddPolygonToolbar` in `Viewer.tsx`
   * (`srid={projectData.project.srid}`) — rather than fetched here, so this
   * dialog stays independent of a `QueryClientProvider` for reads.
   */
  srid: number;
}

/** Sentinel picker value meaning "create a new VECTOR layer to hold the saved crest". */
const NEW_LAYER_OPTION = '__new__';

/**
 * Sentinel picker value for "no alignment chosen as the guide".
 *
 * Radix's `SelectItem` THROWS on `value=''` (it reserves the empty string for
 * "nothing selected", which is what the placeholder renders), so the
 * no-guide entry cannot simply be the empty-valued `<option>` this picker
 * used to carry. Same trick, same reason, as `NEW_LAYER_OPTION` above: a
 * value that can never collide with a real alignment key (those are
 * `layerId:featureId`). It maps to `guideKey === null` in both directions.
 */
const NO_GUIDE_OPTION = '__none__';

/**
 * Sentinel for "no analysis boundary". Deliberately the SAME literal the
 * deviation and surface-difference dialogs use for their boundary pickers, so
 * the three read identically; there, the form value is `''` and `'__none__'`
 * exists only for Radix's benefit, and it is the same here.
 */
const NO_BOUNDARY_OPTION = '__none__';

/**
 * True for the empty payload Radix's hidden native `<select>` echoes back.
 *
 * A `Select` that sits inside a `<form>` — which every field in this dialog now
 * does — makes Radix render a visually-hidden native `<select>` alongside it
 * (`SelectBubbleInput`, so autofill and form reset keep working). When the
 * value changes, that component assigns it to the real DOM element and
 * dispatches a `change` event, and `Select` feeds the event's value straight
 * back into `onValueChange`. If the matching `<option>` has not been registered
 * yet (options register from the items' own effects, a tick behind a value set
 * programmatically) the DOM select cannot hold the value, reports `''`, and the
 * echo WIPES the selection — which is exactly what killed the sole-alignment
 * auto-select the moment this dialog gained a `<form>`.
 *
 * `''` can never be a legitimate choice here: Radix throws on
 * `<SelectItem value=''>`, so every item in every picker below carries a
 * non-empty value, and "none" is spelled `'__none__'`. An empty payload is
 * therefore always the echo and never the user.
 */
const isNativeSelectEcho = (value: string) => value === '';

/** Flatten one run's `Float64Array` of XYZ triples into vertex tuples. */
function runToCoords(points: Float64Array): Coord3[] {
  const coords: Coord3[] = [];
  for (let i = 0; i < points.length / 3; i++) {
    coords.push([points[i * 3], points[i * 3 + 1], points[i * 3 + 2]]);
  }
  return coords;
}

// `as const satisfies` (not a plain `Record<CrestWarning, string>` annotation):
// the react-i18next typed `t()` overload requires a literal key it can check
// against the known resource shape, and a `: Record<..., string>` annotation
// would widen every value back to plain `string` and defeat that check.
const WARNING_KEY = {
  GUIDE_OFF_SURFACE: 'crestLine.warnGuideOffSurface',
  RUNS_TOO_SHORT: 'crestLine.warnRunsTooShort',
  MOSTLY_REJECTED: 'crestLine.warnMostlyRejected',
  ALL_FLAT: 'crestLine.warnAllFlat',
  OUTSIDE_BOUNDARY: 'crestLine.warnOutsideBoundary',
} as const satisfies Record<CrestWarning, string>;

// GUIDE_OFF_SURFACE / RUNS_TOO_SHORT / MOSTLY_REJECTED / OUTSIDE_BOUNDARY are
// actionable problems with the run itself (red, matching the other analysis
// dialogs' `blocking` styling); ALL_FLAT is the spec §9 "honest disclosure"
// that the surface genuinely has no high point anywhere — informational, not
// an error (amber).
const WARNING_TONE: Record<CrestWarning, string> = {
  GUIDE_OFF_SURFACE: 'text-red-600 dark:text-red-400',
  RUNS_TOO_SHORT: 'text-red-600 dark:text-red-400',
  MOSTLY_REJECTED: 'text-red-600 dark:text-red-400',
  ALL_FLAT: 'text-amber-600 dark:text-amber-400',
  OUTSIDE_BOUNDARY: 'text-red-600 dark:text-red-400',
};

/**
 * Hide the enclosing `DialogContent` for as long as `active` is true, and put
 * it back afterwards.
 *
 * Draw mode has to leave the viewer COMPLETELY clear: the user is tracing a
 * line across the embankment and the dialog is centred right on top of it.
 * Every affordance moves to `CrestGuideToolbar` (a `FloatingToolbar` pinned
 * top-centre inside the viewer), so there is nothing left for the dialog to
 * show — but it cannot simply render `null`, because the card, its border and
 * its close button belong to `DialogContent`, which `DialogProvider` owns.
 * The component stays mounted throughout, so all of its state (params, guide
 * choice, the live 3D preview) survives the round trip.
 *
 * It hides the container by walking one level up from an anchor node rather
 * than by pushing a class through the provider: the anchor is always a DIRECT
 * child of `DialogContent` (`DialogProvider` renders `{item.content}` straight
 * into `DialogContent`, adding no wrapper element of its own), and this keeps
 * the mechanism inside the one dialog that needs it instead of in a provider
 * every other dialog also pays for.
 *
 * `display: none` as an inline style, not the `hidden` attribute: the UA's
 * `[hidden] { display: none }` rule is a USER-AGENT declaration and loses to
 * `DialogContent`'s own author-level `grid` class, so `hidden` would leave the
 * dialog fully visible. An inline style outranks the class.
 */
function useHideEnclosingDialog(active: boolean) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!active) return;
    const el = anchorRef.current?.parentElement;
    if (!el) return;
    el.style.display = 'none';
    return () => {
      el.style.removeProperty('display');
    };
  }, [active]);
  return anchorRef;
}

/**
 * The secondary knobs, inside the shared `AdvancedSection` ("Advanced
 * settings") like every run dialog's — each one a full-width `FormField` with
 * its own `FormDescription`, never a bespoke two-column grid of bare labels.
 */
interface AdvancedField {
  key: keyof CrestFormValues & keyof CrestParams;
  labelKey: string;
  helpKey: string;
  step: string;
  min: string;
}

// Same `as const satisfies` reasoning as WARNING_KEY above: keeps each
// `labelKey`/`helpKey` as its specific literal type for the typed `t()` calls.
const ADVANCED_FIELDS = [
  {
    key: 'stationInterval',
    labelKey: 'crestLine.stationInterval',
    helpKey: 'crestLine.stationIntervalHelp',
    step: '0.5',
    min: '0.1',
  },
  {
    key: 'offsetStep',
    labelKey: 'crestLine.offsetStep',
    helpKey: 'crestLine.offsetStepHelp',
    step: '0.05',
    min: '0.01',
  },
  {
    key: 'flatTolerance',
    labelKey: 'crestLine.flatTolerance',
    helpKey: 'crestLine.flatToleranceHelp',
    step: '0.005',
    min: '0',
  },
  {
    key: 'topSlopeThreshold',
    labelKey: 'crestLine.topSlopeThreshold',
    helpKey: 'crestLine.topSlopeThresholdHelp',
    step: '0.01',
    min: '0.01',
  },
  {
    key: 'simplifyTolerance',
    labelKey: 'crestLine.simplifyTolerance',
    helpKey: 'crestLine.simplifyToleranceHelp',
    step: '0.005',
    min: '0',
  },
  {
    key: 'minRunLength',
    labelKey: 'crestLine.minRunLength',
    helpKey: 'crestLine.minRunLengthHelp',
    step: '1',
    min: '0',
  },
] as const satisfies AdvancedField[];

/**
 * `guideKey` and `boundaryLayerId` carry the picker sentinels' MEANING, not
 * the sentinels themselves: `guideKey` is `NO_GUIDE_OPTION` for "no guide"
 * (Radix cannot express it as `''`), while `boundaryLayerId` is `''` for "no
 * boundary" — the exact shape the deviation / surface-difference dialogs use,
 * so the payload-side handling reads the same across all three.
 */
const schema = z.object({
  guideKey: z.string(),
  boundaryLayerId: z.string(),
  targetLayerId: z.string().min(1),
  corridorHalfWidth: z.number().positive(),
  stationInterval: z.number().positive(),
  offsetStep: z.number().positive(),
  flatTolerance: z.number().min(0),
  topSlopeThreshold: z.number().positive(),
  simplifyTolerance: z.number().min(0),
  minRunLength: z.number().min(0),
});

type CrestFormValues = z.infer<typeof schema>;

export function CrestLineDialog({ sourceLayerId, projectId, srid }: CrestLineDialogProps) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const gate = useFeature('analysis.crestLine');
  const checkFeature = useFeatureCheck();
  const sources = useAtomValue(crestSourcesAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const layers = useAtomValue(layersAtom);
  const setPreview = useSetAtom(crestPreviewAtom);
  const requestCrestExport = useSetAtom(crestExportRequestAtom);
  const [guideMode, setGuideMode] = useAtom(crestGuideModeAtom);
  const draft = useAtomValue(crestGuideDraftAtom);
  const cancelGuideSketch = useSetAtom(crestGuideCancelAtom);
  const queryClient = useQueryClient();
  // Get the dialog off the centreline entirely while the user is drawing —
  // you cannot click a line the dialog is sitting on, and `CrestGuideToolbar`
  // now carries the status and every action.
  const drawAnchorRef = useHideEnclosingDialog(guideMode);

  const form = useForm<CrestFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      guideKey: NO_GUIDE_OPTION,
      boundaryLayerId: '',
      targetLayerId: NEW_LAYER_OPTION,
      corridorHalfWidth: DEFAULT_CREST_PARAMS.corridorHalfWidth,
      stationInterval: DEFAULT_CREST_PARAMS.stationInterval,
      offsetStep: DEFAULT_CREST_PARAMS.offsetStep,
      flatTolerance: DEFAULT_CREST_PARAMS.flatTolerance,
      topSlopeThreshold: DEFAULT_CREST_PARAMS.topSlopeThreshold,
      simplifyTolerance: DEFAULT_CREST_PARAMS.simplifyTolerance,
      minRunLength: DEFAULT_CREST_PARAMS.minRunLength,
    },
  });
  // Subscribed, like the run dialogs: `form.formState.errors` read during
  // render is a proxy the React Compiler can memoise past.
  const { errors } = useFormState({ control: form.control });

  // `useWatch`, not `form.watch()`: this render (and the live preview it
  // drives) has to re-run on every keystroke in a NumericField.
  //
  // Watched BY NAME, one call per field, rather than as a single nameless
  // `useWatch({ control })`: the nameless form hands back the whole values
  // object and, on the first render, that object is not yet seeded from
  // `defaultValues` — every field reads `undefined`, which made the guide
  // picker open on its placeholder and stopped the sole-alignment auto-select
  // (whose guard is `guideKey !== null`) from ever firing. A named watch reads
  // through `_defaultValues` and is correct from the first render.
  const control = form.control;
  const watchedGuideKey = useWatch({ control, name: 'guideKey' });
  const boundaryLayerId = useWatch({ control, name: 'boundaryLayerId' });
  const targetLayerId = useWatch({ control, name: 'targetLayerId' });
  const corridorHalfWidth = useWatch({ control, name: 'corridorHalfWidth' });
  const stationInterval = useWatch({ control, name: 'stationInterval' });
  const offsetStep = useWatch({ control, name: 'offsetStep' });
  const flatTolerance = useWatch({ control, name: 'flatTolerance' });
  const topSlopeThreshold = useWatch({ control, name: 'topSlopeThreshold' });
  const simplifyTolerance = useWatch({ control, name: 'simplifyTolerance' });
  const minRunLength = useWatch({ control, name: 'minRunLength' });

  const [drawnGuide, setDrawnGuide] = useState<Guide | null>(null);
  const [saving, setSaving] = useState(false);

  const vectorLayers = useMemo(() => layers.filter((l) => l.type === 'VECTOR'), [layers]);

  const source = sources.get(sourceLayerId) ?? null;

  const guideKey = watchedGuideKey === NO_GUIDE_OPTION ? null : watchedGuideKey;

  // Rebuild `CrestParams` from the watched primitives, memoised on them so
  // `result` below only recomputes when a parameter really changed.
  // `tieTolerance` is not exposed in the UI — it is a numerical detail of the
  // relief scan, not a knob a surveyor should be turning.
  const params = useMemo<CrestParams>(
    () => ({
      corridorHalfWidth,
      stationInterval,
      offsetStep,
      flatTolerance,
      topSlopeThreshold,
      simplifyTolerance,
      minRunLength,
      tieTolerance: DEFAULT_CREST_PARAMS.tieTolerance,
    }),
    [corridorHalfWidth, stationInterval, offsetStep, flatTolerance, topSlopeThreshold, simplifyTolerance, minRunLength],
  );

  // Whether the user has made an EXPLICIT guide choice yet — picking an
  // alignment, picking the "no guide" item, or committing a drawn guide all
  // count. Once true, the sole-alignment auto-select below must never fire
  // again for the lifetime of this component instance, or picking
  // `NO_GUIDE_OPTION` (which sets `guideKey` back to `null`, same as the
  // "nothing chosen yet" state) would snap straight back to the alignment
  // and the user could never clear the guide (Finding 4).
  //
  // A ref, not state: flipping it must not itself trigger a re-render or
  // re-run any effect (the auto-select effect below only needs to READ it at
  // the moment it would otherwise fire) — using state here would add a
  // second effect-triggering value with no additional behaviour, just more
  // opportunities for the two to fall out of sync. It cannot leak across a
  // reopen either: `CrestLineDialog` is only ever mounted while its stack
  // entry exists in `DialogProvider` (`dialogStackAtom`), which is REMOVED
  // (not merely hidden) on close, so re-opening pushes a fresh stack entry,
  // remounts the component from scratch, and `useRef(false)` starts over.
  const userChoseGuideRef = useRef(false);

  // Default to the sole alignment when exactly one is registered: it is
  // almost always the one meant, and it makes the dialog useful on open
  // rather than presenting an empty picker every time (SDD ruling R5). Only
  // while the user hasn't made an explicit choice yet — see
  // `userChoseGuideRef` above for why an explicit "no guide" must stick.
  useEffect(() => {
    if (guideKey !== null || userChoseGuideRef.current) return;
    const keys = [...alignments.keys()];
    if (keys.length === 1) form.setValue('guideKey', keys[0]);
  }, [alignments, guideKey, form]);

  // The guide picker's validity effect, the twin of `boundaryLayerId`'s below:
  // an alignment can disappear while this dialog is open (its layer deleted,
  // its feature removed, `GeoJson.tsx` re-registering without it). Without
  // this, the picker keeps showing the stale key while `alignments.get(key)`
  // answers `undefined` — so `guide` is null, there is no preview, no stats,
  // and nothing on screen explains why.
  //
  // Deliberately does NOT set `userChoseGuideRef`: a disappearance is not a
  // user choice. If the user had never chosen (the sole-alignment auto-select
  // put this key here), the auto-select above is free to fire again on
  // whatever remains; if the user HAD chosen explicitly, the ref is already
  // true and the dialog stays on "no guide" instead of snapping to some other
  // alignment they never picked.
  useEffect(() => {
    if (watchedGuideKey !== NO_GUIDE_OPTION && !alignments.has(watchedGuideKey)) {
      form.setValue('guideKey', NO_GUIDE_OPTION);
    }
  }, [watchedGuideKey, alignments, form]);

  // Leaving draw mode with a non-empty draft is the commit signal (Enter,
  // handled by CrestGuideTool) — convert the sketch into a Guide and let it
  // supersede any alignment selection. Escape also leaves draw mode, but it
  // clears the draft first (also in CrestGuideTool), so it never lands here.
  // Drawing a guide is itself an explicit choice (Finding 4) — it must not
  // be overwritten by the sole-alignment auto-select on some later render.
  useEffect(() => {
    if (guideMode || draft.length < CREST_GUIDE_MIN_VERTICES) return;
    userChoseGuideRef.current = true;
    setDrawnGuide(guideFromXY(draft));
    form.setValue('guideKey', NO_GUIDE_OPTION);
  }, [guideMode, draft, form]);

  // Memoise on the guide ALIGNMENT OBJECT, never on the `alignments` Map.
  // `registerAlignmentsAtom` allocates a fresh Map on every write and has no
  // identity bail-out (unlike `registerCrestSourcesAtom`), and `onSubmit`'s
  // `finally` invalidates `['features', layerId]` — so the refetch that
  // follows a partial-failure retry hands this component a new Map (a fully
  // successful save unmounts the dialog before any of this matters). Keying
  // the memo on the Map would then allocate a new `Guide` (guideFromAlignment
  // builds a fresh Float64Array every call) → new `result` → `savedRunsRef`
  // resets and a full extraction re-runs, duplicating the runs the failed
  // attempt already persisted. GeoJson.tsx's content-key guard keeps an
  // unchanged `Alignment` object referentially stable across that refetch, so
  // pinning to the object holds.
  const guideAlignment = guideKey ? (alignments.get(guideKey) ?? null) : null;
  const guide = useMemo(() => {
    if (drawnGuide) return drawnGuide;
    return guideAlignment ? guideFromAlignment(guideAlignment) : null;
  }, [drawnGuide, guideAlignment]);

  // ------------------------------------------------------------- boundary
  //
  // Deviation and surface difference crop server-side (`scripts/boundary.py`,
  // via a `boundary.geojson` written by preprocess). Crest extraction runs in
  // the browser, so the same crop has to happen here — see
  // `@/lib/crestLine/boundary` for the rule-by-rule correspondence.
  //
  // Excluded candidates mirror the deviation dialog's (`allLayers.filter(l =>
  // l.type === 'VECTOR' && l.id !== source && l.id !== reference)`): the
  // surface being extracted, and the layer the crest is about to be written
  // INTO — cropping a crest to the layer it is being saved to is never what
  // anyone means, and while the crest is unsaved that layer is empty anyway.
  const boundaryCandidates = useMemo(
    () => layers.filter((l) => l.type === 'VECTOR' && l.id !== sourceLayerId && l.id !== targetLayerId),
    [layers, sourceLayerId, targetLayerId],
  );
  useEffect(() => {
    if (boundaryLayerId && !boundaryCandidates.some((l) => l.id === boundaryLayerId)) {
      form.setValue('boundaryLayerId', '');
    }
  }, [boundaryLayerId, boundaryCandidates, form]);

  // Same query key the viewer fills for every vector layer
  // (`['features', layerId]` in `Viewer.tsx`), so picking a boundary layer
  // that is already displayed costs nothing.
  const boundaryQuery = useQuery({
    queryKey: ['features', boundaryLayerId],
    queryFn: () => loadFeaturesByLayerOfflineAware([boundaryLayerId], projectId),
    enabled: !!boundaryLayerId,
  });
  const boundaryPolys = useMemo(
    () => (boundaryLayerId ? boundaryPolygonsFromFeatures(boundaryQuery.data ?? []) : []),
    [boundaryLayerId, boundaryQuery.data],
  );
  // Pending / failed / empty are three DIFFERENT states, and only ONE of them
  // (empty) may go on to run unclipped.
  //
  // A boundary layer whose features have not arrived yet must NOT be silently
  // treated as "no boundary": that would flash the unclipped crest into the
  // 3D scene and then swap it. No result until we know what we are clipping to.
  const boundaryPending = !!boundaryLayerId && boundaryQuery.isPending;
  // A boundary layer that could not be FETCHED at all is blocking, matching
  // what deviation does server-side: `resolveBoundary` (preprocess.ts) returns
  // an `{ error }` that fails the whole job rather than letting it run
  // unclipped. Running unclipped here would be worse than the client/server
  // disagreement the twin comment in `lib/crestLine/boundary.ts` exists to
  // prevent — the save would go on to record a `boundaryLayerId` in
  // `properties.crest` for a clip that never happened, i.e. a provenance
  // record that lies. Without this, a rejected query leaves `isPending` false
  // and `data` undefined, so `boundaryPolys` is `[]` and the predicate is
  // `null`: indistinguishable, downstream, from "this layer has no polygons".
  const boundaryError = !!boundaryLayerId && boundaryQuery.isError;
  // `boundary.py` raises `ValueError(EMPTY_MESSAGE)` when a boundary carries
  // no polygon and no closed polyline; the client cannot fail a job, so it
  // says so in place and runs unclipped. Explicitly NOT the failure case
  // above — "this layer holds no polygons" is a different and, for a fetch
  // that never landed, untrue statement.
  const boundaryEmpty = !!boundaryLayerId && !boundaryPending && !boundaryError && boundaryPolys.length === 0;

  /**
   * The predicate that is actually APPLIED, and the only layer id that may be
   * recorded as provenance — produced together, by one memo, so the two can
   * never disagree.
   *
   * `properties.crest.boundaryLayerId` must describe what was applied, never
   * what the picker happens to be showing: `layerId` here is non-null only
   * when `predicate` is, so a boundary that failed to load, or resolved to no
   * polygons at all, is recorded as `null` — the extraction genuinely ran
   * unclipped and the saved feature says so. Belt and braces on top of the
   * blocking `boundaryError` guard below: even if a future refactor lets an
   * unclipped run reach Save, it cannot claim a clip.
   */
  const appliedBoundary = useMemo(() => {
    const predicate = boundaryError ? null : makeInsideBoundary(boundaryPolys);
    return { predicate, layerId: predicate ? boundaryLayerId || null : null };
  }, [boundaryError, boundaryPolys, boundaryLayerId]);

  // The dialog owns turning draw mode on — this is one of the four entry
  // points `analysis.crestLine` must cover (spec: gate every activation
  // path, not just the panel that hosts it).
  const startDrawing = () => {
    const g = checkFeature('analysis.crestLine');
    if (!g.allowed) {
      g.promptUpgrade();
      return;
    }
    setGuideMode(true);
  };

  // The live preview IS the feature's value — extractCrestLine must never
  // run for an ungated user. Gating only the menu item (Task 11) would leave
  // the real thing open, so this check happens at the computation itself,
  // not just around the JSX that displays its result.
  const result = useMemo(() => {
    // `boundaryError` is as blocking as `boundaryPending`: the user asked for
    // a clip we could not build, and an unclipped crest is not an acceptable
    // substitute for it (see `boundaryError` above).
    if (!gate.allowed || !source || !guide || boundaryPending || boundaryError) return null;
    return extractCrestLine(source.sampler, guide, params, appliedBoundary.predicate ?? undefined);
  }, [gate.allowed, source, guide, params, appliedBoundary, boundaryPending, boundaryError]);

  // Publish the preview while the dialog is open; clear it on unmount (and
  // on every recompute, via the cleanup) so a cancelled dialog or a stale
  // in-flight computation never leaves a crest ribbon in the 3D scene.
  useEffect(() => {
    setPreview(result);
    return () => setPreview(null);
  }, [result, setPreview]);

  // If the dialog closes (Escape at the dialog level, navigating away, …)
  // while the draw tool is still active, don't leave it orphaned in the
  // viewer with no dialog left to consume its draft.
  useEffect(() => {
    // The same write Escape / the toolbar's Cancel performs: discard the
    // sketch (both mirrors) and leave draw mode.
    return () => cancelGuideSketch();
    // Deliberately empty deps: this must fire only on the dialog's own
    // unmount, not on every guideMode/cancelGuideSketch identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which run indices of the CURRENT `result` have already been persisted.
  // Keyed by `result`'s own identity (not e.g. a counter) so a fresh
  // extraction — a new `result` object from the `useMemo` above, triggered
  // by a guide/param change — starts a clean batch, while retrying the SAME
  // `result` after a partial failure resumes rather than re-creating
  // features that already exist. A ref, not state: it's write-once
  // bookkeeping consumed only inside the submit handler, not something the
  // render path needs to react to.
  const savedRunsRef = useRef<{ result: CrestResult | null; indices: Set<number>; featureIds: string[] }>({
    result: null,
    indices: new Set(),
    featureIds: [],
  });

  // Persist every extracted run as its own LineString feature. NEVER batch
  // the runs into one MultiLineString: featureToAlignment welds a
  // MultiLineString's parts with concatMultiLine, which would bridge the
  // real surface gaps between runs into a single, geometrically bogus
  // alignment. One toast.loading tracks the whole batch through to
  // toast.success/toast.error (this dialog issues synchronous mutations
  // directly, not the long-running-job tracker pattern used by
  // RunCompareSurfacesDialog/useDeviationProgress — there is no server
  // batch job here to poll).
  //
  // Resumability: a mid-batch failure (run 3 of 5 throws) has already
  // persisted runs 1-2 via their own `createFeatureMutation` calls — those
  // are real, permanent rows. `savedRunsRef` records which run indices
  // succeeded so a second Save skips them and creates only the remainder,
  // instead of re-running the whole batch and duplicating runs 1-2.
  // Invalidation happens in `finally`, not just the success path: whatever
  // was actually written (even a 2-of-5 partial batch) must become visible
  // to the user, or a partial success looks indistinguishable from total
  // failure.
  // Takes no submitted-values argument on purpose: the one form value that
  // used to be read from the submitted snapshot — `boundaryLayerId` — is now
  // read from `appliedBoundary`, which records what was actually applied
  // rather than what the picker was showing.
  const onSubmit = async () => {
    if (!result || result.runs.length === 0 || !source || saving) return;
    if (savedRunsRef.current.result !== result) {
      savedRunsRef.current = { result, indices: new Set(), featureIds: [] };
    }
    const saved = savedRunsRef.current.indices;
    const featureIds = savedRunsRef.current.featureIds;

    setSaving(true);
    const toastId = toast.loading(t('crestLine.saving'));
    // `form.getValues`, not the submitted snapshot, once the layer has been
    // created below: a retry must reuse the layer the first attempt made.
    let layerId = form.getValues('targetLayerId');
    let layerName = vectorLayers.find((l) => l.id === layerId)?.name ?? '';
    let createdNewLayer = false;
    try {
      if (layerId === NEW_LAYER_OPTION) {
        layerName = `${source.layerName} crest`;
        const created = await execute(createLayerMutation, {
          input: { projectId, name: layerName, type: 'VECTOR' as LayerType },
        });
        if (!created?.createLayer?.id) throw new Error(t('crestLine.failed'));
        layerId = created.createLayer.id;
        createdNewLayer = true;
        form.setValue('targetLayerId', layerId);
      }

      for (let i = 0; i < result.runs.length; i++) {
        if (saved.has(i)) continue;
        const coords = runToCoords(result.runs[i].points);
        const geometry = buildLineGeometry([coords], srid);
        const wgs84 = await projectPartsToWGS84([coords], srid);
        const name = `${source.layerName} crest ${i + 1}`;
        const properties = {
          name,
          // Presence of this object is what makes the line a first-class
          // alignment — GeoJson.tsx reads it to register into alignmentsAtom.
          alignment: { name, staStart: 0 },
          // Provenance: how this line was produced, so it can be traced
          // back to the surface and parameters that made it. `surfaceNames`
          // is spec §10's "surface" field: the sampler spans every <Surface>
          // in the source file and picks positionally, so recording WHICH
          // surfaces were in play is the only way a saved line can be
          // re-derived (or its ambiguity spotted) after the fact.
          // `params` stays plain data — the boundary rides alongside it as a
          // layer id, never as the predicate that was applied.
          //
          // `appliedBoundary.layerId`, NOT the form's `boundaryLayerId`: this
          // field asserts that the saved line was clipped to that layer, so it
          // may only name a boundary whose predicate really ran. A layer that
          // failed to load, or that holds no polygons, records `null`.
          crest: {
            sourceLayerId,
            sourceLayerName: source.layerName,
            surfaceNames: [...source.surfaceNames],
            params,
            boundaryLayerId: appliedBoundary.layerId,
            extractedAt: new Date().toISOString(),
          },
        };
        const input: any = { layerId, geometry, properties };
        if (wgs84) input.geometryWGS84 = buildLineGeometry(wgs84, 4326);
        const res = await execute(createFeatureMutation, { input });
        if (!res?.createFeature) throw new Error(t('crestLine.failed'));
        featureIds[i] = res.createFeature.id;
        saved.add(i);
      }

      // Scope the export affordance to exactly the runs from THIS result —
      // never the whole layer, which may hold unrelated pre-existing features.
      const savedFeatureIds = featureIds.filter((id): id is string => !!id);
      toast.success(t('crestLine.saved'), {
        id: toastId,
        // Long enough to actually click after glancing away — matches
        // `useReportProgress`'s completed-report toast, the other place a
        // toast action drives a follow-up export.
        duration: 10000,
        action: {
          label: t('crestLine.exportDxf'),
          onClick: () => requestCrestExport({ layerId, layerName, featureIds: savedFeatureIds }),
        },
      });
      // The whole batch is in — nothing left to retry, so close like every
      // other save flow. `Layers.tsx` picks up the export request above
      // independently of this dialog's lifecycle.
      dismissActiveDialog();
    } catch (e: any) {
      // Tell the user where they stand: a partial batch (>0 runs already
      // persisted) is a recoverable state — pressing Save again finishes it
      // — which is a different message from "nothing was saved".
      if (saved.size > 0) {
        toast.error(t('crestLine.failedPartial', { saved: saved.size, total: result.runs.length }), { id: toastId });
      } else {
        toast.error(e?.message || t('crestLine.failed'), { id: toastId });
      }
    } finally {
      setSaving(false);
      // Whatever was actually written must become visible, whether this
      // call finished the whole batch or failed partway through it.
      if (createdNewLayer) await queryClient.invalidateQueries({ queryKey: ['layers'] });
      if (layerId !== NEW_LAYER_OPTION) await queryClient.invalidateQueries({ queryKey: ['features', layerId] });
    }
  };

  if (!gate.allowed) {
    // Same top-level shape as the allowed branch below (and as
    // RunClearanceDialog): DialogHeader/DialogTitle as a sibling of the body,
    // not nested inside it — Radix's DialogContent needs a DialogTitle
    // descendant for its accessible-name contract regardless of which branch
    // renders, and this is the branch an unentitled user actually sees.
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('crestLine.title')}</DialogTitle>
          <DialogDescription>{t('crestLine.description')}</DialogDescription>
        </DialogHeader>
        {/* Same shape as the run dialogs' own early-return
            branch: a short body paragraph, then the actions in a real
            `DialogFooter` — not a loose `div` of buttons, which is the one
            place in this dialog that was still off the house layout. */}
        <p data-testid='crestline-locked' className='py-4 text-sm text-slate-700 dark:text-stone-200'>
          {t('crestLine.description')}
        </p>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => dismissActiveDialog()}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
          {/* Visually muted, never the HTML `disabled` attribute — a disabled
              button swallows the click, and this button's whole job is to
              open the upgrade dialog (documented gate rule). */}
          <Button
            type='button'
            variant='outline'
            className='opacity-60'
            data-testid='crestline-upgrade'
            onClick={gate.promptUpgrade}
          >
            {t('crestLine.title')}
          </Button>
        </DialogFooter>
      </>
    );
  }

  // Draw mode: the dialog disappears completely. Every affordance the user
  // needs while sketching — the live vertex count, Undo, Finish, Cancel —
  // lives in `CrestGuideToolbar`, pinned top-centre inside the viewer, so
  // there is nothing left worth showing here and a card parked over the line
  // being traced is exactly the defect. Every hook above still runs, so the
  // live preview in the 3D scene keeps updating and the committed guide is
  // picked up by the mode:false effect the moment Finish/Enter lands.
  //
  // `useHideEnclosingDialog` (above) is what actually removes the container;
  // this branch just keeps the subtree minimal and supplies its anchor.
  if (guideMode) {
    return (
      <>
        {/* Radix wires `aria-labelledby` to a DialogTitle descendant and warns
            without one, so the title stays in the tree even though the whole
            container is display:none for the duration. */}
        <DialogHeader className='sr-only'>
          <DialogTitle>{t('crestLine.title')}</DialogTitle>
        </DialogHeader>
        {/* Anchor: a direct child of `DialogContent`, which is the element
            `useHideEnclosingDialog` hides. Carries the test id the draw-mode
            tests assert on. */}
        <span ref={drawAnchorRef} data-testid='crestline-guide-drawing' hidden />
      </>
    );
  }

  const advancedHasError = ADVANCED_FIELDS.some((f) => !!errors[f.key]);
  const vertexCount = result ? result.runs.reduce((n, r) => n + r.points.length / 3, 0) : 0;
  const canSave = !!result && result.runs.length > 0 && !saving;

  return (
    <>
      <Form {...form}>
        {/* The run-analysis dialogs' body/scroll structure, verbatim from
            `RunCompareSurfacesDialog`: the form is the flex column that owns
            the height cap, the middle div is the ONLY scroll container, and
            the footer is pinned outside it.

            `min-w-0` at every level and `overflow-x-hidden` on the scroller
            are load-bearing, not decoration. A flex/grid item's automatic
            minimum size is `min-content`, so without `min-w-0` an item refuses
            to shrink below its content's intrinsic width; and `overflow-y-auto`
            ALONE computes `overflow-x` to `auto` (CSS Overflow §3: `visible`
            beside a non-`visible` value becomes `auto`), which is what turned
            the smallest horizontal overshoot into a real scrollbar. */}
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='flex max-h-[80vh] min-h-0 min-w-0 flex-1 flex-col overflow-clip'
        >
          {/* `min-w-0` on the header lets its grid track shrink below the
              intrinsic width of a very long source-layer name — without it the
              name pegs the dialog's width and the inputs overflow.
              `break-words` is what actually lets an unbreakable file name
              (`Kupferzell_Damm_Entwurf_2024-05.xml`) wrap instead of pushing
              the scroll container wide. Same fix, same reason, as
              `RunCompareSurfacesDialog`'s header. */}
          <DialogHeader className='min-w-0'>
            <DialogTitle>{t('crestLine.title')}</DialogTitle>
            <DialogDescription className='break-words'>
              {source ? (
                <>
                  {t('crestLine.source')}: <strong>{source.layerName}</strong>
                </>
              ) : (
                t('crestLine.description')
              )}
            </DialogDescription>
          </DialogHeader>

          <div className='flex min-w-0 flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-1 py-4'>
            <AnalysisSection title={t('analysisConfig.sections.inputs')} testId='crestline-section-inputs'>
            {/* Which surfaces the sampler actually resolves over. `LandXML.tsx`
                builds ONE sampler per layer over every <Surface> in the file
                and answers with the first one that covers the point, so a
                Civil 3D export carrying "Existing Ground" next to the design
                surface is genuinely ambiguous. Spec §9 wanted a SurfacePicker;
                until there is one, say plainly what is being sampled and, when
                there is more than one candidate, that the choice is positional
                — never resolve it silently. */}
            {source && source.surfaceNames.length > 0 && (
              <div className='min-w-0'>
                <p data-testid='crestline-surfaces' className='text-xs break-words text-slate-500 dark:text-stone-400'>
                  {t('crestLine.surfaces', {
                    count: source.surfaceNames.length,
                    names: source.surfaceNames.join(', '),
                  })}
                </p>
                {source.surfaceNames.length > 1 && (
                  <p
                    data-testid='crestline-surfaces-ambiguous'
                    className='mt-1 text-xs break-words text-amber-600 dark:text-amber-400'
                  >
                    {t('crestLine.surfacesAmbiguous')}
                  </p>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name='guideKey'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('crestLine.guide')}</FormLabel>
                  <div className='flex min-w-0 gap-2'>
                    {/* House picker, not a raw `<select>`. `NO_GUIDE_OPTION`
                        keeps the "no guide" entry SELECTABLE — Radix forbids an
                        empty item value, and a placeholder alone can never be
                        chosen again once the user has picked an alignment, so
                        clearing the guide would become impossible. */}
                    <Select
                      value={field.value}
                      onValueChange={(key) => {
                        if (isNativeSelectEcho(key)) return;
                        // Picking anything here — including the no-guide item
                        // — is an explicit choice; it must stick even when it
                        // lands on `guideKey === null`, the same value the
                        // sole-alignment auto-select treats as "nothing chosen
                        // yet".
                        userChoseGuideRef.current = true;
                        field.onChange(key);
                        setDrawnGuide(null);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger data-testid='crestline-guide' className='w-full min-w-0'>
                          <SelectValue placeholder={t('crestLine.guideAlignment')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_GUIDE_OPTION} data-testid='crestline-guide-option-none'>
                          {t('crestLine.guideNone')}
                        </SelectItem>
                        {[...alignments.values()].map((a) => (
                          <SelectItem key={a.key} value={a.key} data-testid={`crestline-guide-option-${a.key}`}>
                            <span className='min-w-0 flex-1 truncate'>{a.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* No `guideMode` variant/disabled state here any more:
                        this form is only ever rendered when draw mode is OFF.
                        It stays beside the picker rather than in the footer —
                        it is the second way to answer this one field, not a
                        dialog-level action. */}
                    <Button type='button' variant='outline' data-testid='crestline-guide-draw' onClick={startDrawing}>
                      {t('crestLine.guideDraw')}
                    </Button>
                  </div>
                  <FormDescription>{t('crestLine.guideHelp')}</FormDescription>
                  {/* No in-form drawing hint: while `guideMode` is true this
                      whole dialog is hidden and `CrestGuideToolbar` carries the
                      status. */}
                  {drawnGuide && (
                    <p data-testid='crestline-guide-drawn' className='text-xs text-slate-500 dark:text-stone-400'>
                      {t('crestLine.guideDrawnPoints', { count: drawnGuide.length / 2 })}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='boundaryLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(INPUT_ROLE_LABEL_KEYS.boundary)}{' '}
                    <span className='font-normal text-slate-500 dark:text-stone-400'>{t('analysisConfig.optional')}</span>
                  </FormLabel>
                  <Select
                    onValueChange={(v) => {
                      if (isNativeSelectEcho(v)) return;
                      field.onChange(v === NO_BOUNDARY_OPTION ? '' : v);
                    }}
                    value={field.value || NO_BOUNDARY_OPTION}
                  >
                    <FormControl>
                      <SelectTrigger data-testid='crestline-boundary' className='w-full min-w-0'>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* The run dialogs' plain "None" (`InputRoleField`'s
                          optional item) — role-neutral, so it fits a dialog
                          that clips a surface rather than a point cloud. */}
                      <SelectItem value={NO_BOUNDARY_OPTION} data-testid='crestline-boundary-option-none'>
                        {t('analysisConfig.none')}
                      </SelectItem>
                      {boundaryCandidates.map((l) => (
                        <SelectItem key={l.id} value={l.id} data-testid={`crestline-boundary-option-${l.id}`}>
                          <span className='min-w-0 flex-1 truncate'>{l.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('boundary.help')}</FormDescription>
                  {/* Amber, informational: the layer loaded fine, it just has
                      nothing to clip with, and the run continues unclipped
                      (with `properties.crest.boundaryLayerId` recording
                      `null`, since nothing was applied). */}
                  {boundaryEmpty && (
                    <p data-testid='crestline-boundary-empty' className='text-xs text-amber-600 dark:text-amber-400'>
                      {t('crestLine.boundaryEmpty')}
                    </p>
                  )}
                  {/* Red, blocking: nothing is extracted and Save is disabled
                      until the boundary loads or the user picks another
                      layer. Deliberately a DIFFERENT message from the empty
                      note above — "this layer holds no polygons" is not
                      something we know when the fetch never landed. */}
                  {boundaryError && (
                    <p data-testid='crestline-boundary-error' className='text-xs text-red-600 dark:text-red-400'>
                      {t('crestLine.boundaryFailed')}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.settings')} testId='crestline-section-settings'>
            <FormField
              control={form.control}
              name='corridorHalfWidth'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('crestLine.corridorHalfWidth')}</FormLabel>
                  <FormControl>
                    <NumericField
                      data-testid='crestline-corridor'
                      value={field.value}
                      onChange={field.onChange}
                      step='0.5'
                      min='1'
                    />
                  </FormControl>
                  <FormDescription>{t('crestLine.corridorHalfWidthHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            </AnalysisSection>

            <AnalysisSection title={t('analysisConfig.sections.output')} testId='crestline-section-output'>
              <OutputSummary
                testId='crestline-output'
                creates={[
                  targetLayerId === NEW_LAYER_OPTION
                    ? t('analysisConfig.creates.crestLinesNewLayer', { count: result?.runs.length ?? 0 })
                    : t('analysisConfig.creates.crestLines', {
                        count: result?.runs.length ?? 0,
                        layer: vectorLayers.find((l) => l.id === targetLayerId)?.name ?? '',
                      }),
                ]}>
            <FormField
              control={form.control}
              name='targetLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('crestLine.targetLayer')}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      if (isNativeSelectEcho(v)) return;
                      field.onChange(v);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger data-testid='crestline-target-layer' className='w-full min-w-0'>
                        <SelectValue placeholder={t('crestLine.newLayer')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={NEW_LAYER_OPTION}>{t('crestLine.newLayer')}</SelectItem>
                      {vectorLayers.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          <span className='min-w-0 flex-1 truncate'>{l.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>{t('crestLine.targetLayerHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

              </OutputSummary>
            {result && (
              <div className='flex min-w-0 flex-col gap-1'>
                <p data-testid='crestline-stats' className='text-xs text-slate-500 dark:text-stone-400'>
                  {t('crestLine.stats', {
                    stations: result.stationsSampled,
                    runs: result.runs.length,
                    vertices: vertexCount,
                  })}
                </p>
                {/* The section-split readout is the honest disclosure spec §9
                    requires: say plainly whether this surface has a high point
                    at all, rather than silently resolving the ambiguity in the
                    line that gets drawn. */}
                <p data-testid='crestline-section-split' className='text-xs text-slate-500 dark:text-stone-400'>
                  {t('crestLine.sectionSplit', { relief: result.reliefSections, flat: result.flatSections })}
                </p>
                {result.warning && (
                  <p data-testid='crestline-warning' className={`text-xs ${WARNING_TONE[result.warning]}`}>
                    {t(WARNING_KEY[result.warning])}
                  </p>
                )}
              </div>
            )}
            </AnalysisSection>

            <AdvancedSection testId='crestline-advanced' hasError={advancedHasError}>
              {ADVANCED_FIELDS.map((f) => (
                <FormField
                  key={f.key}
                  control={form.control}
                  name={f.key}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t(f.labelKey)}</FormLabel>
                      <FormControl>
                        <NumericField
                          data-testid={`crestline-${f.key}`}
                          value={field.value as number}
                          onChange={field.onChange}
                          step={f.step}
                          min={f.min}
                        />
                      </FormControl>
                      <FormDescription>{t(f.helpKey)}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </AdvancedSection>
          </div>

          <DialogFooter className='mt-4 shrink-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
            <Button type='button' variant='outline' onClick={() => dismissActiveDialog()} disabled={saving}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' data-testid='crestline-save' disabled={!canSave}>
              {saving ? t('crestLine.saving') : t('crestLine.save')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
